<script setup>
import { ref, watch, onMounted } from "vue";
import { useLanguageStore } from "@/pinia";
import axios from "@/axios";
import { formatDate } from "@/plugins/dayjs";

const languageStore = useLanguageStore();

const props = defineProps({
  videoId: {
    type: String,
    default: ""
  }
});

const transferLogs = ref([]);
const loading = ref(false);

const fetchTransferLogs = async () => {
  if (!props.videoId) {
    transferLogs.value = [];
    return;
  }

  loading.value = true;
  try {
    const response = await axios.get(`/admin/transfer/logs/${props.videoId}`);
    transferLogs.value = response.data || [];
  } catch (error) {
    console.error("전송 이력 조회 실패:", error);
    transferLogs.value = [];
  } finally {
    loading.value = false;
  }
};

const getStatusColor = (result) => {
  return result ? "green" : "red";
};

const getStatusIcon = (result) => {
  return result ? "mdi-check-circle" : "mdi-close-circle";
};

const getStatusText = (result) => {
  return result
      ? languageStore.get("transferSuccess")
      : languageStore.get("transferFailed");
};

watch(
    () => props.videoId,
    (newVal) => {
      if (newVal) {
        fetchTransferLogs();
      }
    },
    { immediate: true }
);
</script>

<template>
  <div class="transfer-history-container">
    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <div v-if="!loading && transferLogs.length === 0" class="no-data">
      {{ languageStore.get("noTransferHistory") }}
    </div>

    <v-table v-if="!loading && transferLogs.length > 0" density="compact" class="transfer-table">
      <thead>
        <tr>
          <th>{{ languageStore.get("transferDateTime") }}</th>
          <th>{{ languageStore.get("protocol") }}</th>
          <th>{{ languageStore.get("transferTarget") }}</th>
          <th>{{ languageStore.get("path") }}</th>
          <th>{{ languageStore.get("status") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="log in transferLogs" :key="log.transferId">
          <td>{{ formatDate(log.transferDt, "YYYY-MM-DD HH:mm:ss") }}</td>
          <td>{{ log.transferType?.toUpperCase() }}</td>
          <td>{{ log.transferIp }}</td>
          <td class="path-cell" :title="log.transferPath">{{ log.transferPath }}</td>
          <td>
            <v-chip
                :color="getStatusColor(log.transferResult)"
                size="small"
                variant="flat"
            >
              <v-icon size="14" class="mr-1">{{ getStatusIcon(log.transferResult) }}</v-icon>
              {{ getStatusText(log.transferResult) }}
            </v-chip>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<style scoped>
.transfer-history-container {
  min-height: 200px;
}

.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.transfer-table {
  width: 100%;
}

.transfer-table th {
  background-color: #f5f5f5;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
}

.transfer-table td {
  vertical-align: middle;
}

.path-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
